import { DataGrid, Chip, theme, Avatar, AvatarSize, TableCell, Card, Icon, IconDictionary } from '@ketch-com/deck'
import { Box, Typography, Divider } from '@mui/material'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { TrackerListItem, TrackerListItemOrigin } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'
import { originCopy } from 'pages/dataSentry/trackers/constants'
import { formatToK } from 'pages/dataSentry/utils/transformPieChartData'

export const Overview: React.FC = () => {
  const { trackerOverview, trackersList } = useDataSentryContext()

  const columns: GridColDef<TrackerListItem>[] = [
    {
      align: 'left',
      field: 'dataSystem',
      headerName: 'System',
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="label" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <Box display="flex" gap={1} alignItems="center">
            {row.dataSystem?.logoUrl ? (
              <Avatar
                src={row.dataSystem?.logoUrl}
                alt={row.dataSystem?.name}
                size={AvatarSize.large}
                borderColor={theme.palette.Black.o8}
                sx={{ padding: '6px' }}
              />
            ) : (
              <Avatar
                icon="NoLogo"
                size={AvatarSize.large}
                textColor={theme.palette.Black.o48}
                isLogo
                borderColor={theme.palette.Black.o8}
                sx={{ padding: '6px' }}
              />
            )}
            <Typography variant="body">{getSystemName(row?.dataSystem || {})}</Typography>
          </Box>
        )
      },
    },
    {
      align: 'left',
      field: 'categories',
      headerName: 'Categories',
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="label" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            {row?.categories?.length ? (
              <Box display="flex" gap={1} flexWrap="wrap">
                {row?.categories?.map(category => (
                  <Chip key={category?.code} size="small" label={category?.name || category?.code} />
                ))}
              </Box>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'origin',
      headerName: 'Origin',
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="label" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            <Typography>{originCopy[row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED]}</Typography>
          </TableCell>
        )
      },
    },

    {
      align: 'left',
      field: 'purposes',
      headerName: 'Purposes',
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="label" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            {row?.purposes?.length ? (
              <Box display="flex" gap={1} flexWrap="wrap">
                {row?.purposes?.map(purpose => (
                  <Chip key={purpose?.code} size="small" label={purpose?.name || purpose?.code} />
                ))}
              </Box>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
  ]

  const percentage =
    trackerOverview?.urlSeenCount && trackersList?.totalUrlSeenCount
      ? Math.round((trackerOverview.urlSeenCount / trackersList.totalUrlSeenCount) * 100)
      : 0

  const totalUrls = formatToK(trackersList?.totalUrlSeenCount || 0)

  return (
    <Box display="flex" gap={3} flexDirection="column">
      <DataGrid
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
            backgroundImage: 'unset',
            cursor: 'auto',
          },
        }}
        autosizeOnMount
        autosizeOptions={{
          expand: true,
          includeHeaders: true,
          includeOutliers: false,
        }}
        getRowHeight={() => 'auto'}
        columns={columns}
        getRowId={row => row.name}
        rows={trackerOverview ? [trackerOverview] : []}
        checkboxSelection={false}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        disableColumnResize
        disableChildrenSorting={false}
        disableRowHoverStates
        noRowsOverlayProps={{ buttonTitle: '' }}
      />

      <Divider />

      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="label" color={theme.palette.Text.Secondary}>
          Seen On
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Box width={200} mt={2}>
            <Card
              sx={{
                border: `1px solid ${theme.palette.Black.o8}`,
                backgroundColor: theme.palette.White.o100,
                padding: 2,
              }}
              customContentCard={
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="h3">
                      {percentage} %{' '}
                      <sup>
                        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
                          {totalUrls}
                        </Typography>
                      </sup>
                    </Typography>

                    <Typography variant="label">of all URLs</Typography>
                  </Box>
                  <Icon name={IconDictionary.Link} width="32px" height="32px" iconColor={theme.palette.Black.o48} />
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
